// Nova pessoa = login + ficha + organograma num formulário só (28/09/2026).
//
// Substitui o "Criar usuário" de Permissões e o cadastro solto de profissional:
// o servidor (pessoa_criar) cria a conta no Cognito PRIMEIRO e grava a ficha,
// o vínculo de RH, a 1.ª vigência de remuneração e os perfis numa transação
// só — e desfaz a conta se o banco falhar. Aqui só se conversa:
//   · ficha parecida sem login → o servidor devolve `candidatas` e a pessoa
//     escolhe "usar essa ficha" (religar) ou "é outra pessoa";
//   · e-mail já ligado a uma ficha → oferece religar essa;
//   · quem não tem a permissão financeira não vê salário/coeficiente (a
//     Marina completa na ficha; até lá a pessoa aparece "sem remuneração").
// "Conta sem vínculo de trabalho" (contador, TI, serviço) usa a porta antiga
// com a marca — não cria ficha.
import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Checkbox, DatePicker, Flex, Input, InputNumber, Modal, Select, Space, Tag, Typography,
  App as AntApp,
} from 'antd';
import dayjs from 'dayjs';
import { acaoPessoa, ErroPessoa, type Opcoes } from './apiPessoas';

const { Text } = Typography;

export type ResultadoNovaPessoa = {
  profissional_id?: string; email: string; nome?: string; conta_criada?: boolean;
  senha_temp?: string | null; email_enviado?: boolean; religados?: number;
  pendencia_remuneracao?: boolean; perfis_atribuidos?: string[]; perfis_nexo_ignorados?: string[];
  conta_sem_vinculo?: boolean;
};

export default function ModalNovaPessoa({
  aberto, onClose, onSalvo, emailInicial, nomeInicial, religar,
}: {
  aberto: boolean;
  onClose: () => void;
  onSalvo: (r: ResultadoNovaPessoa) => void;
  /** Login que já existe e está sem ficha (Cassiano): o e-mail vem preenchido. */
  emailInicial?: string;
  nomeInicial?: string;
  /** Ficha que já existe e está sem login (Lucas, Felipe): cria/liga o login a ela. */
  religar?: { id: string; nome: string } | null;
}) {
  const { message } = AntApp.useApp();
  const [opcoes, setOpcoes] = useState<Opcoes | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoNovaPessoa | null>(null);
  const [candidatas, setCandidatas] = useState<Array<{ id: string; nome: string; ativo: boolean }>>([]);
  const [jaTem, setJaTem] = useState<{ profissional_id: string; nome: string; ativo: boolean; error?: string } | null>(null);
  const [religarId, setReligarId] = useState<string | null>(religar?.id || null);
  const [religarNome, setReligarNome] = useState<string | null>(religar?.nome || null);
  const [semVinculo, setSemVinculo] = useState(false);
  const [f, setF] = useState({
    email: emailInicial || '', nome: nomeInicial || religar?.nome || '', telefone: '', senha_temp: '',
    cargo: '', setor_id: undefined as string | undefined, chefe_profissional_id: undefined as string | undefined,
    contrato_tipo: undefined as string | undefined, admissao: dayjs().format('YYYY-MM-DD'), horas_dia: 8,
    perfis: [] as string[], salario: null as number | null, coeficiente: 1, motivo_sem_vinculo: '',
  });
  const set = (k: keyof typeof f, v: any) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!aberto) return;
    setResultado(null); setCandidatas([]); setJaTem(null);
    setReligarId(religar?.id || null); setReligarNome(religar?.nome || null);
    setF((p) => ({ ...p, email: emailInicial || p.email, nome: nomeInicial || religar?.nome || p.nome }));
    acaoPessoa<Opcoes>('pessoa_opcoes').then((o) => {
      setOpcoes(o);
      if (o.hoje) setF((p) => ({ ...p, admissao: p.admissao || o.hoje }));
    }).catch((e) => message.error('Não foi possível carregar as opções: ' + e.message));
  }, [aberto, emailInicial, nomeInicial, religar, message]);

  const setores = useMemo(() => (opcoes?.setores || []).filter((s) => s.ativo).map((s) => ({ value: s.id, label: s.nome })), [opcoes]);
  const chefes = useMemo(() => (opcoes?.chefes || []).map((c) => ({ value: c.id, label: `${c.nome}${c.setor ? ` · ${c.setor}` : ''}` })), [opcoes]);
  const tipos = useMemo(() => (opcoes?.tipos_vinculo || []).map((t) => ({ value: t, label: t })), [opcoes]);
  const perfis = opcoes?.perfis || [];

  async function novoSetor() {
    const nome = window.prompt('Nome do setor novo:');
    if (!nome) return;
    try {
      const r = await acaoPessoa<{ setor: { id: string; nome: string } }>('pessoa_setor_salvar', { nome });
      const o = await acaoPessoa<Opcoes>('pessoa_opcoes');
      setOpcoes(o); set('setor_id', r.setor.id);
    } catch (e: any) { message.error(e.message); }
  }

  async function salvar(extra: Record<string, any> = {}) {
    if (!f.email.includes('@')) { message.warning('Informe o e-mail da pessoa.'); return; }
    if (!f.nome.trim()) { message.warning('Informe o nome completo.'); return; }
    setSalvando(true);
    try {
      if (semVinculo) {
        const r = await acaoPessoa('admin_perms_criar_usuario', {
          email: f.email.trim(), nome: f.nome.trim(), telefone: f.telefone || undefined,
          senha_temp: f.senha_temp || undefined, perfis_codigos: f.perfis, conta_sem_vinculo: true,
          motivo_sem_vinculo: f.motivo_sem_vinculo || undefined,
        });
        const res: ResultadoNovaPessoa = { ...r, conta_sem_vinculo: true, conta_criada: true };
        setResultado(res); onSalvo(res);
        return;
      }
      const corpo: Record<string, any> = {
        email: f.email.trim(), nome: f.nome.trim(), telefone: f.telefone || undefined, senha_temp: f.senha_temp || undefined,
        cargo: f.cargo || undefined, setor_id: f.setor_id, chefe_profissional_id: f.chefe_profissional_id,
        contrato_tipo: f.contrato_tipo, admissao: f.admissao, horas_dia: f.horas_dia, perfis_codigos: f.perfis,
        ...(opcoes?.pode_salario ? { salario: f.salario, coeficiente: f.coeficiente } : {}),
        ...(religarId ? { religar_profissional_id: religarId } : {}),
        ...extra,
      };
      const r = await acaoPessoa<ResultadoNovaPessoa>('pessoa_criar', corpo);
      setResultado(r); setCandidatas([]); setJaTem(null);
      onSalvo(r);
    } catch (e: any) {
      if (e instanceof ErroPessoa && e.dados?.codigo === 'candidatas') { setCandidatas(e.dados.candidatas || []); return; }
      if (e instanceof ErroPessoa && e.dados?.codigo === 'ja_tem_ficha') { setJaTem(e.dados); return; }
      message.error(e.message, 8);
    } finally { setSalvando(false); }
  }

  const coluna = (titulo: string, children: any) => (
    <div style={{ flex: 1, minWidth: 260 }}>
      <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{titulo}</Text>
      <Flex vertical gap={8} style={{ marginTop: 6 }}>{children}</Flex>
    </div>
  );
  const rotulo = (t: string) => <Text type="secondary" style={{ fontSize: 12 }}>{t}</Text>;

  return (
    <Modal open={aberto} onCancel={() => !salvando && onClose()} footer={null}
      title={religarNome ? `Criar login para ${religarNome}` : emailInicial ? `Completar a ficha de ${emailInicial}` : 'Nova pessoa'}
      width="94vw" style={{ maxWidth: 1080, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' } }} destroyOnHidden>
      {resultado ? (
        <Flex vertical gap={10}>
          <Alert type="success" showIcon
            message={resultado.conta_sem_vinculo ? 'Conta criada (sem vínculo de trabalho).' : religarId ? 'Login criado e ligado à ficha.' : resultado.conta_criada ? 'Pessoa criada: login e ficha ligados.' : 'Ficha criada e ligada ao login que já existia.'}
            description={(
              <div style={{ fontSize: 13 }}>
                <div><strong>Login:</strong> {resultado.email}</div>
                {resultado.senha_temp && (
                  <div><strong>Senha temporária:</strong> <code>{resultado.senha_temp}</code> — {resultado.email_enviado ? 'também foi por e-mail.' : 'o e-mail automático falhou: envie pessoalmente.'} A pessoa troca no primeiro acesso.</div>
                )}
                {!!resultado.religados && <div>{resultado.religados} hora(s) anotada(s) antes do vínculo passaram a contar para esta ficha.</div>}
                {resultado.pendencia_remuneracao && <div style={{ color: '#B07500' }}>Salário e coeficiente ficaram em branco: até a Marina completar na ficha, as horas desta pessoa não viram custo de contrato.</div>}
                {!!resultado.perfis_nexo_ignorados?.length && <div style={{ color: '#B07500' }}>Perfis NEXO de uma conta antiga não foram transferidos: {resultado.perfis_nexo_ignorados.join(', ')}.</div>}
              </div>
            )} />
          <Flex justify="flex-end"><Button type="primary" onClick={onClose}>Fechar</Button></Flex>
        </Flex>
      ) : (
        <Flex vertical gap={12}>
          {!religarId && !emailInicial && (
            <Checkbox checked={semVinculo} onChange={(e) => setSemVinculo(e.target.checked)}>
              É uma <strong>conta sem vínculo de trabalho</strong> (contador, TI, serviço): só login, sem ficha, fora de horas e custo.
            </Checkbox>
          )}
          {religarNome && (
            <Alert type="info" showIcon message={`O login vai ser ligado à ficha "${religarNome}" (as horas já anotadas com esse e-mail passam a contar para ela).`}
              action={<Button size="small" onClick={() => { setReligarId(null); setReligarNome(null); }}>Não, criar ficha nova</Button>} />
          )}
          {candidatas.length > 0 && (
            <Alert type="warning" showIcon message="Já existe ficha parecida sem login. É a mesma pessoa?"
              description={(
                <Space wrap>
                  {candidatas.map((c) => (
                    <Button key={c.id} size="small" onClick={() => { setReligarId(c.id); setReligarNome(c.nome); setCandidatas([]); salvar({ religar_profissional_id: c.id }); }}>
                      Sim, usar "{c.nome}"{c.ativo ? '' : ' (inativa)'}
                    </Button>
                  ))}
                  <Button size="small" type="primary" onClick={() => { setCandidatas([]); salvar({ ignorar_candidatas: true }); }}>É outra pessoa — criar nova</Button>
                </Space>
              )} />
          )}
          {jaTem && (
            <Alert type="warning" showIcon message={jaTem.error}
              description={<Button size="small" type="primary" onClick={() => { setReligarId(jaTem.profissional_id); setReligarNome(jaTem.nome); setJaTem(null); salvar({ religar_profissional_id: jaTem.profissional_id }); }}>Religar a ficha "{jaTem.nome}"</Button>} />
          )}
          <Flex gap={16} wrap>
            {coluna('Acesso', (
              <>
                <div>{rotulo('E-mail (login)')}<Input value={f.email} disabled={!!emailInicial} onChange={(e) => set('email', e.target.value)} placeholder="fulano@hydrodataeng.com.br" /></div>
                <div>{rotulo('Nome completo')}<Input value={f.nome} onChange={(e) => set('nome', e.target.value)} /></div>
                <div>{rotulo('Telefone (opcional)')}<Input value={f.telefone} onChange={(e) => set('telefone', e.target.value)} placeholder="(51) 9…" /></div>
                {!emailInicial && (
                  <div>{rotulo('Senha temporária (em branco = a padrão da casa)')}<Input value={f.senha_temp} onChange={(e) => set('senha_temp', e.target.value)} placeholder="Hydrodata@2026!" /></div>
                )}
                <div>
                  {rotulo('Perfis de acesso')}
                  <Select mode="multiple" style={{ width: '100%' }} value={f.perfis} onChange={(v) => set('perfis', v)}
                    showSearch optionFilterProp="label" placeholder="Escolha um ou mais"
                    options={perfis.map((p) => ({ value: p.codigo, label: `${p.nome}${p.possui_permissao_nexo ? ' · NEXO' : ''}` }))} />
                </div>
                {semVinculo && (
                  <div>{rotulo('Por que não tem ficha? (opcional)')}<Input value={f.motivo_sem_vinculo} onChange={(e) => set('motivo_sem_vinculo', e.target.value)} placeholder="contador externo, conta de serviço…" /></div>
                )}
              </>
            ))}
            {!semVinculo && coluna('Vínculo', (
              <>
                <div>{rotulo('Cargo')}<Input value={f.cargo} onChange={(e) => set('cargo', e.target.value)} placeholder="Engenheira, Técnico de campo…" /></div>
                <div>
                  {rotulo('Setor')}
                  <Flex gap={6}>
                    <Select style={{ flex: 1 }} value={f.setor_id} onChange={(v) => set('setor_id', v)} allowClear
                      showSearch optionFilterProp="label" options={setores} placeholder="Escreva para buscar" />
                    <Button onClick={novoSetor}>+ setor</Button>
                  </Flex>
                </div>
                <div>
                  {rotulo('Chefe direto (recebe os alertas de horas desta pessoa)')}
                  <Select style={{ width: '100%' }} value={f.chefe_profissional_id} onChange={(v) => set('chefe_profissional_id', v)} allowClear
                    showSearch optionFilterProp="label" options={chefes} placeholder="Escreva para buscar" />
                </div>
                <Flex gap={8} wrap>
                  <div style={{ flex: 1, minWidth: 150 }}>
                    {rotulo('Tipo de vínculo')}
                    <Select style={{ width: '100%' }} value={f.contrato_tipo} onChange={(v) => set('contrato_tipo', v)} allowClear
                      showSearch optionFilterProp="label" options={tipos} placeholder="CLT, PJ…" />
                  </div>
                  <div style={{ minWidth: 150 }}>
                    {rotulo('Admissão')}
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" value={f.admissao ? dayjs(f.admissao) : null}
                      onChange={(d) => set('admissao', d ? d.format('YYYY-MM-DD') : '')} />
                  </div>
                  <div style={{ minWidth: 110 }}>
                    {rotulo('Horas por dia')}
                    <InputNumber style={{ width: '100%' }} min={1} max={24} step={1} value={f.horas_dia} onChange={(v) => set('horas_dia', Number(v) || 8)} />
                  </div>
                </Flex>
              </>
            ))}
            {!semVinculo && opcoes?.pode_salario && coluna('Remuneração (1.ª vigência, desde a admissão)', (
              <>
                <div>{rotulo('Salário / honorário mensal (R$)')}<InputNumber style={{ width: '100%' }} min={0} value={f.salario} onChange={(v) => set('salario', v === null || v === undefined ? null : Number(v))} decimalSeparator="," /></div>
                <div>{rotulo('Coeficiente de encargos (1 a 5)')}<InputNumber style={{ width: '100%' }} min={1} max={5} step={0.05} value={f.coeficiente} onChange={(v) => set('coeficiente', Number(v) || 1)} decimalSeparator="," /></div>
                <Text type="secondary" style={{ fontSize: 11 }}>É o custo real da pessoa que vira custo dos contratos pelas horas anotadas.</Text>
              </>
            ))}
            {!semVinculo && opcoes && !opcoes.pode_salario && coluna('Remuneração', (
              <Alert type="info" showIcon message="Você não tem a permissão financeira: a Marina completa salário e coeficiente na ficha. Até lá as horas da pessoa não viram custo." />
            ))}
          </Flex>
          <Flex justify="flex-end" gap={8}>
            {semVinculo && <Tag color="orange">sem ficha</Tag>}
            <Button onClick={onClose} disabled={salvando}>Cancelar</Button>
            <Button type="primary" loading={salvando} onClick={() => salvar()}>
              {semVinculo ? 'Criar conta' : religarId ? 'Criar login e ligar' : emailInicial ? 'Criar ficha e ligar' : 'Criar pessoa'}
            </Button>
          </Flex>
        </Flex>
      )}
    </Modal>
  );
}
